import {
  createContext,
  use,
  useId,
  useRef,
  useState,
  type ComponentPropsWithRef,
  type KeyboardEvent,
  type ReactNode,
} from "react";

import { cn } from "../lib/cn";
import { useSlidingIndicator } from "../lib/use-sliding-indicator";

interface TabsContextValue {
  value: string;
  setValue: (value: string) => void;
  baseId: string;
}

const TabsContext = createContext<TabsContextValue | null>(null);

function useTabs(component: string) {
  const context = use(TabsContext);
  if (!context) throw new Error(`<${component}> doit être utilisé à l'intérieur de <Tabs>`);
  return context;
}

export interface TabsProps extends Omit<ComponentPropsWithRef<"div">, "onChange"> {
  /** Mode contrôlé. */
  value?: string;
  defaultValue: string;
  onChange?: (value: string) => void;
}

/**
 * Onglets.
 *
 * Le clavier suit le motif « onglets automatiques » du WAI-ARIA APG :
 * flèches pour naviguer, Home/Fin pour les extrémités, et le panneau suit le
 * focus. C'est cette partie — pas le style — qui justifie un composant partagé :
 * une réimplémentation maison l'oublie systématiquement.
 */
export function Tabs({ className, value, defaultValue, onChange, children, ...props }: TabsProps) {
  const baseId = useId();
  const [internal, setInternal] = useState(defaultValue);
  const isControlled = value !== undefined;
  const current = isControlled ? value : internal;

  const setValue = (next: string) => {
    if (!isControlled) setInternal(next);
    onChange?.(next);
  };

  return (
    <TabsContext value={{ value: current, setValue, baseId }}>
      <div className={cn("flex flex-col gap-4", className)} {...props}>
        {children}
      </div>
    </TabsContext>
  );
}

export interface TabListProps extends ComponentPropsWithRef<"div"> {
  /** `line` : soulignement (défaut). `enclosed` : pilules sur fond en retrait. */
  variant?: "line" | "enclosed";
  "aria-label": string;
}

export function TabList({ className, variant = "line", children, ...props }: TabListProps) {
  const listRef = useRef<HTMLDivElement>(null);
  const { value } = useTabs("TabList");
  const { rect, animated } = useSlidingIndicator(listRef, value, '[role="tab"][aria-selected="true"]');

  /** Déplace le focus d'un onglet à l'autre ; `click()` synchronise la sélection. */
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const keys = ["ArrowLeft", "ArrowRight", "Home", "End"];
    if (!keys.includes(event.key)) return;

    const tabs = Array.from(
      listRef.current?.querySelectorAll<HTMLButtonElement>('[role="tab"]:not(:disabled)') ?? [],
    );
    const index = tabs.findIndex((tab) => tab === document.activeElement);
    if (index === -1) return;

    event.preventDefault();
    const last = tabs.length - 1;
    const next =
      event.key === "Home"
        ? 0
        : event.key === "End"
          ? last
          : event.key === "ArrowLeft"
            ? (index - 1 + tabs.length) % tabs.length
            : (index + 1) % tabs.length;

    tabs[next]?.focus();
    tabs[next]?.click();
  };

  return (
    <div
      ref={listRef}
      role="tablist"
      onKeyDown={onKeyDown}
      className={cn(
        "group relative flex items-center gap-1 overflow-x-auto scrollbar-none touch-manipulation [webkit-overflow-scrolling:touch]",
        variant === "line" && "border-b border-border",
        variant === "enclosed" && "w-fit rounded-lg bg-surface-sunken p-1 inset-shadow-control",
        className,
      )}
      data-variant={variant}
      {...props}
    >
      {/*
        Un seul indicateur partagé qui GLISSE d'un onglet à l'autre.

        Les deux variantes ne se déplacent pas de la même façon, et c'est
        délibéré :

        · `line` est un trait de 2 px. Il occupe toute la largeur de la barre et
          se réduit en `scaleX` : position ET largeur tiennent alors dans une
          seule transformation, entièrement composée par le GPU, sans une seule
          passe de mise en page. L'écrasement d'un rayon de 1 px est invisible.

        · `enclosed` est une pastille bordée et ombrée. Un `scaleX` y écraserait
          les arrondis de façon très visible, on interpole donc sa largeur —
          mais `contain-layout` enferme ce recalcul dans l'élément, qui n'a
          aucun enfant : le coût est celui d'une boîte isolée, pas d'un reflow.
          (`contain-layout` et non `contain-paint` : ce dernier rognerait
          l'ombre portée de la pastille.)
      */}
      {rect && (
        <span
          aria-hidden="true"
          className={cn(
            "pointer-events-none absolute left-0 origin-left transform-gpu",
            animated && "will-change-transform",
            variant === "line"
              ? [
                  "-bottom-px h-0.5 w-full rounded-full bg-accent",
                  animated && "transition-transform duration-slow ease-standard",
                ]
              : [
                  "inset-y-1 rounded-md bg-surface-raised shadow-xs contain-layout",
                  animated && "transition-[transform,width] duration-slow ease-standard",
                ],
          )}
          style={
            variant === "line"
              ? {
                  transform: `translate3d(${rect.left}px, 0, 0) scaleX(${
                    rect.container > 0 ? rect.width / rect.container : 0
                  })`,
                }
              : { transform: `translate3d(${rect.left}px, 0, 0)`, width: `${rect.width}px` }
          }
        />
      )}

      {children}
    </div>
  );
}

export interface TabProps extends Omit<ComponentPropsWithRef<"button">, "value"> {
  value: string;
  /** Compteur ou pastille affiché après le libellé. */
  badge?: ReactNode;
}

export function Tab({ className, value, badge, children, ...props }: TabProps) {
  const { value: current, setValue, baseId } = useTabs("Tab");
  const selected = current === value;

  return (
    <button
      type="button"
      role="tab"
      id={`${baseId}-tab-${value}`}
      aria-selected={selected}
      aria-controls={`${baseId}-panel-${value}`}
      tabIndex={selected ? 0 : -1}
      onClick={() => setValue(value)}
      className={cn(
        "relative z-10 inline-flex items-center gap-2 rounded-md px-3.5 py-2.5 sm:px-3 sm:py-2 text-sm font-medium whitespace-nowrap touch-manipulation",
        "transform-gpu transition-[color,transform] duration-fast ease-standard active:scale-[0.98]",
        "focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring",
        "disabled:pointer-events-none disabled:opacity-50",
        selected ? "text-fg" : "text-fg-muted hover:text-fg",
        className,
      )}
      {...props}
    >
      {children}
      {badge}
    </button>
  );
}

export interface TabPanelProps extends ComponentPropsWithRef<"div"> {
  value: string;
}

export function TabPanel({ className, value, children, ...props }: TabPanelProps) {
  const { value: current, baseId } = useTabs("TabPanel");
  if (current !== value) return null;

  return (
    <div
      role="tabpanel"
      id={`${baseId}-panel-${value}`}
      aria-labelledby={`${baseId}-tab-${value}`}
      // Le panneau doit être focusable pour que Tab, depuis l'onglet, y entre.
      tabIndex={0}
      className={cn("focus-visible:outline-none", className)}
      {...props}
    >
      {children}
    </div>
  );
}
